import { flatten as flattenTree } from '@/utils/tree.js'
import { filterChartRowByTimeScale, encodeDate, addTimeDelta } from '@/utils/dates.js'

function getConstrainedBreakdownDimension(slot) {
  function optionsDecorator(options, filter = false) {
    if (!options) return null
    return options
      .filter(opt => !opt.notApplicable)
      .map(opt => {
        return {
          ...opt,
          children: optionsDecorator(opt.children, filter),
          isValid: !filter || filter[opt.id] === true,
        }
      })
  }

  return function() {
    const cubeId = this[slot + 'CubeId']
    const config = this.$store.state.external.versionInfo.cubes.find(c => c.metaId === cubeId)
    const isConstrained = config?.constrained === true

    const isNDSC = /DF_NDSC$/i.test(this[slot + 'CubeId'])
    if (isNDSC && this.$store.state.query.chart === 'heatmap') {
      return []
    }

    if (!this[slot + 'Constraints']) {
      return this.$store.state.cubeMeta[slot + 'BreakdownDimensions']
    }

    return this.$store.state.cubeMeta[slot + 'BreakdownDimensions']
      .filter(d => !d.notApplicable)
      .map(d => {
        const constraints = this[slot + 'Constraints'].find(c => c.id === d.id)

        if (!constraints) return d

        const filter = constraints
          ? Object.fromEntries(constraints.values.map(v => [v, true]))
          : false

        let options = optionsDecorator(d.options, filter).filter(opt =>
          isConstrained ? opt.isValid : true
        )

        return { ...d, options }
      })
  }
}

function chartDataGetterGenerator(slot) {
  return function() {
    const data = this.$store.getters[slot + 'ChartData']
    if (data.length === 0) return data

    const timeScale = this.timeScale.id

    const rawBrkDims = this.$store.state.cubeMeta[slot + 'BreakdownDimensions']
    const brkSelection = { ...this[slot + 'Breakdowns'] }
    const brkDims = [...this[slot + 'BreakdownDimensions']]
    rawBrkDims.forEach(dim => {
      if (!brkDims.map(d => d.id).includes(dim.id)) {
        brkDims.push(dim)
        brkSelection[dim.id] = dim.options[0].id
      }
    })
    if (brkDims.length === 0) {
      return [data[0]].concat(data.slice(1).filter(d => filterChartRowByTimeScale(d[1], timeScale)))
    }

    const filter = Object.keys(brkSelection).reduce((acum, id) => {
      const dim = brkDims.find(dim => dim.id === id)
      if (!dim) return acum
      acum[dim.id] = brkSelection[id]
      return acum
    }, {})
    const lollipopRef = this.lollipopDimension?.id
    const lollipopOpts = new Set(this.$store.state.query.lollipopOpts)

    const isNDSC = /DF_NDSC$/i.test(this[slot + 'CubeId'])
    const isHeatmap = this.chartType === 'heatmap'
    const isLollipop = this.chartType === 'barchart' && !isNDSC

    return [data[0]].concat(
      data.slice(1).filter(row => {
        if (this[slot + 'TimeScales'].length > 1) {
          const valid = filterChartRowByTimeScale(row[1], timeScale)
          if (!valid) return false
        }

        if (this[slot + 'Units'].length > 1) {
          const valid = row[4] === this[slot + 'Unit']?.id
          if (!valid) return false
        }

        return Object.keys(row.brkDims).reduce((valid, k) => {
          if (isHeatmap) {
            return (
              valid && (filter[k] === row.brkDims[k] || (isNDSC && row.brkDims.DIMENSION !== '_T'))
            )
          } else if (isLollipop) {
            let isValid = filter[k] === row.brkDims[k]
            if (lollipopRef === k) {
              isValid ||= lollipopOpts.has(row.brkDims[k])
            }
            return valid || isValid
          } else {
            return valid && (filter[k] === row.brkDims[k] || (isNDSC && k === 'POLICY'))
          }
        }, !isLollipop)
      })
    )
  }
}

export default {
  computed: {
    lng() {
      return this.$route.params.lng
    },
    loading() {
      return (
        (this.$store.state.query.mainCubeId !== null && this.$store.state.data.main === null) ||
        (this.$route.params.mode === 'compare' &&
          this.$store.state.query.pairCubeId !== null &&
          this.$store.state.data.pair === null) ||
        (this.$route.params.mode === 'compare' &&
          this.$store.state.query.sizeCubeId !== null &&
          this.$store.state.data.size === null)
      )
    },
    mainIndId() {
      return this.$store.state.query.mainIndId
    },
    pairIndId() {
      return this.$store.state.query.pairIndId
    },
    sizeIndId() {
      return this.$store.state.query.sizeIndId
    },
    mainCubeId() {
      return this.$store.state.query.mainCubeId
    },
    pairCubeId() {
      return this.$store.state.query.pairCubeId
    },
    sizeCubeId() {
      return this.$store.state.query.sizeCubeId
    },
    flipDimensions() {
      const filter = [this.$store.getters.mainCountryDimension.id].concat(
        this.mainBreakdownDimensions.map(d => d.id)
      )
      return this.$store.getters.mainDimensions.filter(d => filter.includes(d.id))
    },
    mainBreakdowns() {
      return this.$store.state.query.mainBreakdowns
    },
    pairBreakdowns() {
      return this.$store.state.query.pairBreakdowns
    },
    sizeBreakdowns() {
      return this.$store.state.query.sizeBreakdowns
    },
    power() {
      return slot => this.$store.getters.power(slot)
    },
    countryFilter() {
      return this.$store.state.query.countryFilter && this.countrySelection.length > 0
    },
    mainTimeScales() {
      return this.$store.getters.mainTimeScales
    },
    pairTimeScales() {
      return this.$store.getters.pairTimeScales
    },
    sizeTimeScales() {
      return this.$store.getters.sizeTimeScales
    },
    timeScale: {
      get() {
        const fallbackValue = {
          id: 'A', // P1Y',
          name: 'Annual',
          labels: {
            en: 'Annual',
            fr: 'Annuel',
          },
        }
        const timeScale = this.$store.state.query.timeScale
        const mainTimeScale = this.mainTimeScales.find(d => d.id === timeScale)
        const pairTimeScale = this.pairTimeScales.find(d => d.id === timeScale)
        const sizeTimeScale = this.sizeCubeId
          ? this.sizeTimeScales.find(d => d.id === timeScale)
          : true
        if (this.$route.params.mode === 'compare') {
          if (mainTimeScale && pairTimeScale && sizeTimeScale) {
            return mainTimeScale
          } else {
            return fallbackValue
          }
        }

        return mainTimeScale || this.mainTimeScales[0] || fallbackValue
      },
      set(scale) {
        this.$store.commit('timeScale', scale[0].id)
      },
    },
    timeSelection: {
      get() {
        const isNDSC = /DF_NDSC$/i.test(this.mainCubeId)
        let isRange = ['barchart', 'choropleth', 'scatterplot'].indexOf(this.chartType) == -1
        isRange = this.chartType === 'heatmap' ? !isNDSC : isRange
        return isRange ? this.$store.getters.queryTime : this.$store.getters.queryTime[1]
      },
      set(to) {
        const isNDSC = /DF_NDSC$/i.test(this.mainCubeId)
        let isRange = ['barchart', 'choropleth', 'scatterplot'].indexOf(this.chartType) == -1
        isRange = this.chartType === 'heatmap' ? !isNDSC : isRange
        if (isRange) {
          this.$store.commit(
            'time',
            to.map(d => d.getTime())
          )
        } else {
          if (this.mainAvailableDates.length) {
            this.$store.commit('time', [this.mainAvailableDates[0].getTime(), to.getTime()])
          }
        }

        this.$store.commit('utRange', true)
      },
    },
    lastDates: {
      get() {
        let isLastDates = this.$store.state.query.lastDates === true
        if (this.$route.params.mode === 'indicator') {
          isLastDates &= ['barchart', 'choropleth'].includes(this.chartType)
        }

        return Boolean(isLastDates)
      },
      set(to) {
        this.$store.commit('lastDates', to)
        to && this.$store.commit('utRange', !to)
      },
    },
    mainUnits() {
      const dimension = this.$store.getters.mainUnitDimension
      if (!dimension) return []
      const options = Object.fromEntries(this.$store.getters.mainUnits.map(unit => [unit.id, true]))
      return dimension.options
        .filter(opt => options[opt.id])
        .map(opt => {
          opt.label = opt.labels[this.$route.params.lng]
          return opt
        })
    },
    pairUnits() {
      const dimension = this.$store.getters.pairUnitDimension
      if (!dimension) return []
      const options = Object.fromEntries(this.$store.getters.pairUnits.map(unit => [unit.id, true]))
      return dimension.options
        .filter(opt => options[opt.id])
        .map(opt => {
          opt.label = opt.labels[this.$route.params.lng]
          return opt
        })
    },
    sizeUnits() {
      const dimension = this.$store.getters.sizeUnitDimension
      if (!dimension) return []
      const options = Object.fromEntries(this.$store.getters.sizeUnits.map(unit => [unit.id, true]))
      return dimension.options
        .filter(opt => options[opt.id])
        .map(opt => {
          opt.label = opt.labels[this.$route.params.lng]
          return opt
        })
    },
    mainUnit: {
      get() {
        return this.mainUnits.find(u => u.id === this.$store.state.query.mainUnit)
      },
      set(selection) {
        const value = selection[0]
        this.$store.commit('mainUnit', value.id)
      },
    },
    pairUnit: {
      get() {
        return this.pairUnits.find(u => u.id === this.$store.state.query.pairUnit)
      },
      set(selection) {
        const value = selection[0]
        this.$store.commit('pairUnit', value.id)
      },
    },
    sizeUnit: {
      get() {
        return this.sizeUnits.find(u => u.id === this.$store.state.query.sizeUnit)
      },
      set(selection) {
        const value = selection[0]
        this.$store.commit('sizeUnit', value.id)
      },
    },
    countrySelection: {
      get() {
        const query = this.$store.state.query.countries
        return flattenTree(this.mainCountryDimension.options).filter(opt => query.includes(opt.id))
      },
      set(to) {
        this.$store.commit(
          'countries',
          to.map(d => d.id)
        )
      },
    },
    selectedCountries() {
      return Object.fromEntries(this.countrySelection.map(d => [d.id, true]))
    },
    mainBreakdownSelection() {
      return this.mainBreakdownDimensions.reduce((acum, dim) => {
        const options = flattenTree(dim.options)
        acum[dim.id] = options.find(opt => opt.id === this.mainBreakdowns[dim.id]) || options[0]
        return acum
      }, {})
    },
    pairBreakdownSelection() {
      return this.pairBreakdownDimensions.reduce((acum, dim) => {
        const options = flattenTree(dim.options)
        acum[dim.id] = options.find(opt => opt.id === this.pairBreakdowns[dim.id]) || options[0]
        return acum
      }, {})
    },
    sizeBreakdownSelection() {
      return this.sizeBreakdownDimensions.reduce((acum, dim) => {
        const options = flattenTree(dim.options)
        acum[dim.id] = options.find(opt => opt.id === this.sizeBreakdowns[dim.id]) || options[0]
        return acum
      }, {})
    },
    mainCountryDimension() {
      return this.$store.getters.mainCountryDimension
    },
    pairCountryDimension() {
      return this.$store.getters.pairCountryDimension
    },
    sizeCountryDimension() {
      return this.$store.getters.sizeCountryDimension
    },
    mainBreakdownDimensions: getConstrainedBreakdownDimension('main'),
    pairBreakdownDimensions: getConstrainedBreakdownDimension('pair'),
    sizeBreakdownDimensions: getConstrainedBreakdownDimension('size'),
    tableData: function() {
      return slot => this.$store.getters[slot + 'DataAsTable']
    },
    mainChartData: chartDataGetterGenerator('main'),
    pairChartData: chartDataGetterGenerator('pair'),
    sizeChartData: chartDataGetterGenerator('size'),
    mainConstraints() {
      return this.$store.getters.mainConstraints || []
    },
    pairConstraints() {
      return this.$store.getters.pairConstraints || []
    },
    sizeConstraints() {
      return this.$store.getters.sizeConstraints || []
    },
    mainAvailableDates: function() {
      return this.$store.getters.mainAvailableDates.filter(date =>
        filterChartRowByTimeScale(date, this.timeScale.id)
      )
    },
    pairAvailableDates: function() {
      return this.$store.getters.pairAvailableDates.filter(date =>
        filterChartRowByTimeScale(date, this.timeScale.id)
      )
    },
    sizeAvailableDates: function() {
      return this.$store.getters.sizeAvailableDates.filter(date =>
        filterChartRowByTimeScale(date, this.timeScale.id)
      )
    },
    currentChartData() {
      // To overwrite
      return []
    },
    chartType() {
      // To overwrite
      return 'barchart'
    },
    timeSliderValues() {
      const availableDates = this.dataTime || this.mainAvailableDates
      if (availableDates.length === 0) return []
      const ceil = availableDates[availableDates.length - 1]
      let step = availableDates[0]
      const values = []

      do {
        values.push({
          value: step.getTime(),
          label: encodeDate(step),
        })
        step = addTimeDelta(step, this.timeScale.id)
      } while (step <= ceil)

      if (/broadband/i.test(this.$store.state.query.mainCubeId) && this.timeScale.id === 'Q') {
        return values.filter(d => new Date(d.value).getMonth() % 2 === 1)
      }

      return values
    },
    lollipopId() {
      return this.$store.state.query.lollipop || this.mainBreakdownDimensions[0]?.id
    },
    lollipopDimension() {
      return this.mainBreakdownDimensions.find(d => d.id === this.lollipopId)
    },
    lollipopOptions() {
      return this.lollipopDimension
        ? flattenTree(this.lollipopDimension.options).filter(opt =>
            this.$store.state.query.lollipopOpts.includes(opt.id)
          )
        : []
    },
  },
  watch: {
    mainIndId() {
      this.resetTimeSelection('main')
    },
    pairIndId() {
      this.$store.commit('pairData', null)
    },
    sizeIndId() {
      this.$store.commit('sizeData', null)
    },
    timeScale(to, from) {
      if (to.id === from.id) return
      const isRange = ['barchart', 'choropleth', 'scatterplot'].indexOf(this.chartType) == -1
      if (isRange) {
        this.timeSelection = [
          this.mainAvailableDates[0],
          this.mainAvailableDates[this.mainAvailableDates.length - 1],
        ]
      } else {
        this.timeSelection = this.mainAvailableDates[this.mainAvailableDates.length - 1]
      }
    },
  },
  methods: {
    resetTimeSelection(slot) {
      if (
        this.chartType === 'barchart' ||
        this.chartType === 'choropleth' ||
        this.chartType === 'scatterplot'
      ) {
        this.timeSelection = this[slot + 'AvailableDates'][this[slot + 'AvailableDates'].length - 1]
      } else if (this[slot + 'AvailableDates'].length) {
        this.timeSelection = [
          this[slot + 'AvailableDates'][0],
          this[slot + 'AvailableDates'][this[slot + 'AvailableDates'].length - 1],
        ]
      } else {
        this.timeSelection = []
      }
    },
    updateSelection({ slot, dim, value }) {
      if (dim === 'countries') {
        this.countrySelection = value
      } else {
        const commit = slot + 'Breakdowns'
        Object.keys(value).forEach(dimId => {
          if (dimId === this.lollipopId) {
            if (this.lollipopOptions.map(opt => opt.id).includes(value[dimId].id)) {
              this.$store.commit(
                'lollipopOpts',
                this.lollipopOptions.filter(opt => opt.id !== value[dimId].id).map(opt => opt.id)
              )
            }
          }
        })

        this.$store.commit(
          commit,
          Object.keys(value).reduce((acum, dimId) => {
            acum[dimId] = value[dimId].id
            return acum
          }, {})
        )
      }
    },
  },
}
